import {
  BadgeCheck,
  Bell,
  Box,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  CirclePlus,
  Clock,
  CreditCard,
  ExternalLink,
  FileText,
  Folder,
  Image,
  Info,
  LayoutGrid,
  LayoutTemplate,
  LifeBuoy,
  Link,
  Lock,
  Mail,
  Menu,
  Monitor,
  Package,
  Palette,
  Pencil,
  Plug,
  Plus,
  Puzzle,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Store,
  Tag,
  Trash2,
  Truck,
  Upload,
  User,
  Users,
  Wrench,
  X,
} from 'lucide-react'
import type { EmporionIconComponent, EmporionIconName } from './types'

export const EMPORION_ICON_FALLBACK = 'package'

export const EMPORION_ICON_REGISTRY = {
  alert: CircleAlert,
  badge: BadgeCheck,
  bag: ShoppingBag,
  bell: Bell,
  box: Box,
  calendar: CalendarDays,
  cart: ShoppingCart,
  check: Check,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'circle-check': CircleCheck,
  'circle-help': CircleHelp,
  'circle-plus': CirclePlus,
  clock: Clock,
  content: FileText,
  'credit-card': CreditCard,
  edit: Pencil,
  'external-link': ExternalLink,
  file: FileText,
  folder: Folder,
  image: Image,
  info: Info,
  layout: LayoutTemplate,
  'layout-grid': LayoutGrid,
  link: Link,
  lock: Lock,
  mail: Mail,
  menu: Menu,
  monitor: Monitor,
  package: Package,
  palette: Palette,
  plugin: Puzzle,
  plug: Plug,
  plus: Plus,
  search: Search,
  settings: Settings,
  shield: Shield,
  'shield-check': ShieldCheck,
  store: Store,
  support: LifeBuoy,
  tag: Tag,
  trash: Trash2,
  truck: Truck,
  upload: Upload,
  user: User,
  users: Users,
  wrench: Wrench,
  x: X,
} as const satisfies Record<string, EmporionIconComponent>

const hasOwn = (value: string): value is keyof typeof EMPORION_ICON_REGISTRY =>
  Object.prototype.hasOwnProperty.call(EMPORION_ICON_REGISTRY, value)

export const isEmporionIconName = (value: unknown): value is EmporionIconName =>
  typeof value === 'string' && hasOwn(value)

export const resolveEmporionIconName = (
  value: unknown,
  fallbackName: EmporionIconName = EMPORION_ICON_FALLBACK,
): EmporionIconName => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (hasOwn(normalized)) return normalized
  }
  if (typeof fallbackName === 'string') {
    const normalizedFallback = fallbackName.trim().toLowerCase()
    if (hasOwn(normalizedFallback)) return normalizedFallback
  }
  return EMPORION_ICON_FALLBACK
}

export const getEmporionIconComponent = (
  value: unknown,
  fallbackName: EmporionIconName = EMPORION_ICON_FALLBACK,
): EmporionIconComponent => {
  const iconName = resolveEmporionIconName(value, fallbackName) as keyof typeof EMPORION_ICON_REGISTRY
  return EMPORION_ICON_REGISTRY[iconName]
}
